import { useCallback, useMemo, useState } from 'react';
import { useConfigurableColumns, type ColumnPreference, type ConfigurableColumns } from '@immo/components';
import { apiClient } from '@/lib/auth/api-client';
import { useUrlTableSort, type SortDirection, type SortState } from '@/components/sortable-table';
import type { OperatorColumn } from './operator-column';
import type { OperatorColumnKey } from './operator-column-store';

export type OperatorTableOptions<TRow> = {
  /** The rows after the page's own filters — what the table would render unsorted. */
  readonly rows: ReadonlyArray<TRow>;
  readonly preferenceKey: OperatorColumnKey;
  /** Every column, in today's order — which IS the default order. Memoised by the caller. */
  readonly catalogue: ReadonlyArray<OperatorColumn<TRow>>;
  readonly alwaysVisibleId: string;
  readonly lockedLeadingIds?: ReadonlyArray<string>;
  readonly defaultHiddenIds?: ReadonlyArray<string>;
  /** The loader's read. */
  readonly loadedPreference: ColumnPreference;
  readonly describedBy?: string;
};

export type OperatorTable<TRow> = {
  readonly columns: ConfigurableColumns<OperatorColumn<TRow>>;
  readonly sorted: ReadonlyArray<TRow>;
  readonly sort: SortState<string>;
  readonly toggleSort: (field: string) => void;
  readonly setSort: (field: string, direction: SortDirection) => void;
};

export function useOperatorTable<TRow>({
  rows,
  preferenceKey,
  catalogue,
  alwaysVisibleId,
  lockedLeadingIds,
  defaultHiddenIds,
  loadedPreference,
  describedBy,
}: OperatorTableOptions<TRow>): OperatorTable<TRow> {
  // Derived, never seeded (BL-068): the override wins for as long as the page is
  // mounted, so a loader revalidation cannot restore the layout the operator left.
  const [override, setOverride] = useState<ColumnPreference | null>(null);
  const preference = override ?? loadedPreference;

  const savePreference = useCallback(
    async (next: ColumnPreference) => {
      const result = await apiClient.adminPreferences.saveMine({
        pathParams: { key: preferenceKey },
        body: { value: { order: [...next.order], hidden: [...next.hidden] } },
      });

      return result.ok;
    },
    [preferenceKey],
  );

  const columns = useConfigurableColumns({
    catalogue,
    alwaysVisibleId,
    ...(lockedLeadingIds === undefined ? {} : { lockedLeadingIds }),
    ...(defaultHiddenIds === undefined ? {} : { defaultHiddenIds }),
    preference,
    onPreferenceChange: setOverride,
    savePreference,
    ...(describedBy === undefined ? {} : { describedBy }),
  });

  // The full catalogue, not the visible columns: a hidden column keeps sorting.
  const byId = useMemo(() => new Map(catalogue.map((column) => [column.id, column])), [catalogue]);
  const sortKey = useCallback((row: TRow, field: string) => byId.get(field)?.sortValue(row) ?? null, [byId]);

  // The sort lives in the address with the page's filters and search (IMM-1075): a reload, Back or
  // a shared link shows the same order. Unknown column ids in `sort` read as no sort.
  const fields = useMemo(() => catalogue.map((column) => column.id), [catalogue]);
  const { sorted, sort, toggleSort, setSort } = useUrlTableSort(rows, sortKey, { fields });

  return useMemo(() => ({ columns, sorted, sort, toggleSort, setSort }), [columns, sorted, sort, toggleSort, setSort]);
}
